import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material'
import { getDownloadURL, getStorage, ref } from '@firebase/storage'
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import type { AssistantImageCardData, AssistantPhotoItem } from './useAssistantChat'

GlobalWorkerOptions.workerSrc = workerSrc

function useStorageDownloadUrl(storagePath: string | undefined) {
  const [url, setUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')

  useEffect(() => {
    if (!storagePath) {
      setUrl(null)
      setStatus('idle')
      return
    }
    let cancelled = false
    setStatus('loading')
    getDownloadURL(ref(getStorage(), storagePath))
      .then(next => {
        if (!cancelled) {
          setUrl(next)
          setStatus('ready')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUrl(null)
          setStatus('error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [storagePath])

  return { url, status }
}

function kindLabel(kind: AssistantPhotoItem['kind']): string {
  switch (kind) {
    case 'damage_photo':
      return 'Damage'
    case 'photo':
      return 'Photo'
    case 'invoice':
      return 'Invoice'
    case 'estimate':
      return 'Estimate'
    case 'attachment':
      return 'Attachment'
    case 'video':
      return 'Video'
    default:
      return 'Media'
  }
}

function isPdfItem(item: AssistantPhotoItem): boolean {
  if (/\.pdf(\?|$)/i.test(item.storagePath)) return true
  // Repair invoices/estimates are uploaded as PDFs even when the path lacks a clear extension.
  return item.kind === 'invoice' || item.kind === 'estimate'
}

function isLikelyImageItem(item: AssistantPhotoItem): boolean {
  if (isPdfItem(item)) return false
  if (/\.(jpe?g|png|gif|webp|bmp|heic|svg)(\?|$)/i.test(item.storagePath)) return true
  return item.kind === 'damage_photo' || item.kind === 'photo'
}

/** Prefer image for damage/photos; PDF for invoices; attachments follow extension then fall back. */
function previewAsPdf(item: AssistantPhotoItem): boolean {
  if (isPdfItem(item)) return true
  if (isLikelyImageItem(item)) return false
  return item.kind === 'attachment' && !/\.(jpe?g|png|gif|webp|bmp|heic|svg)(\?|$)/i.test(item.storagePath)
}

function downloadFilename(storagePath: string, isPdf: boolean): string {
  const base = storagePath.split('/').pop() || (isPdf ? 'document.pdf' : 'photo.jpg')
  const withoutQuery = base.split('?')[0]
  if (/\.[a-z0-9]+$/i.test(withoutQuery)) return withoutQuery
  return isPdf ? `${withoutQuery}.pdf` : `${withoutQuery}.jpg`
}

async function downloadFromUrl(url: string, filename: string) {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const blob = await response.blob()
  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(downloadUrl)
}

const PdfScrollPreview: React.FC<{ url: string; title: string }> = ({ url, title }) => {
  const hostRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    const host = hostRef.current
    if (!host) return undefined
    let cancelled = false
    setStatus('loading')
    host.replaceChildren()

    const paint = async () => {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = new Uint8Array(await response.arrayBuffer())
      const pdf = await getDocument({ data }).promise
      try {
        if (cancelled) return
        await new Promise<void>(resolve => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
        })
        if (cancelled) return
        const pane = host.parentElement
        const maxWidth = Math.max(240, (pane?.clientWidth || 480) - 32)
        const maxHeight = Math.max(320, (pane?.clientHeight || Math.round(window.innerHeight * 0.62)) - 32)
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (cancelled) return
          const page = await pdf.getPage(pageNumber)
          const unscaled = page.getViewport({ scale: 1 })
          const scale = Math.min(maxWidth / unscaled.width, maxHeight / unscaled.height)
          const viewport = page.getViewport({ scale })
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')
          if (!context) continue
          const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
          canvas.width = Math.floor(viewport.width * pixelRatio)
          canvas.height = Math.floor(viewport.height * pixelRatio)
          canvas.style.width = `${Math.floor(viewport.width)}px`
          canvas.style.height = `${Math.floor(viewport.height)}px`
          canvas.style.display = 'block'
          canvas.style.margin = '12px auto'
          canvas.style.background = '#fff'
          canvas.style.boxShadow = '0 1px 3px rgba(15, 23, 42, 0.16)'
          canvas.setAttribute('aria-label', `${title}, page ${pageNumber.toLocaleString('en-US')} of ${pdf.numPages.toLocaleString('en-US')}`)
          host.appendChild(canvas)
          await page.render({
            canvasContext: context,
            viewport,
            transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0],
          }).promise
        }
        if (!cancelled) setStatus('ready')
      } finally {
        await pdf.destroy()
      }
    }

    paint().catch(() => {
      if (!cancelled) setStatus('error')
    })

    return () => {
      cancelled = true
    }
  }, [title, url])

  return (
    <>
      {status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}
      {status === 'error' ? (
        <Typography variant='body2' color='error' sx={{ p: 2, textAlign: 'center' }}>
          Could not preview this file
        </Typography>
      ) : null}
      <Box ref={hostRef} />
    </>
  )
}

const Thumb: React.FC<{
  item: AssistantPhotoItem
  index: number
  total: number
  onOpen: (index: number) => void
}> = ({ item, index, total, onOpen }) => {
  const { url, status } = useStorageDownloadUrl(item.storagePath)
  const [imgFailed, setImgFailed] = useState(false)
  const pdf = previewAsPdf(item) || imgFailed
  const image = !pdf && isLikelyImageItem(item)

  return (
    <Box
      component='button'
      type='button'
      onClick={() => onOpen(index)}
      aria-label={`Open ${kindLabel(item.kind)} ${index + 1} of ${total}`}
      sx={{
        appearance: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 0,
        m: 0,
        width: 88,
        height: 88,
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        flexDirection: 'column',
        gap: 0.25,
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      {status === 'loading' || status === 'idle' ? (
        <CircularProgress size={20} />
      ) : status === 'error' || !url ? (
        <Typography variant='caption' color='text.secondary' sx={{ px: 0.5, textAlign: 'center' }}>
          {kindLabel(item.kind)}
        </Typography>
      ) : pdf ? (
        <>
          <PictureAsPdfIcon color='error' fontSize='small' />
          <Typography variant='caption' color='text.secondary' sx={{ px: 0.5, textAlign: 'center' }}>
            {kindLabel(item.kind)}
          </Typography>
        </>
      ) : image ? (
        <Box
          component='img'
          src={url}
          alt={item.caption || kindLabel(item.kind)}
          onError={() => setImgFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <>
          <PictureAsPdfIcon color='action' fontSize='small' />
          <Typography variant='caption' color='text.secondary' sx={{ px: 0.5, textAlign: 'center' }}>
            {kindLabel(item.kind)}
          </Typography>
        </>
      )}
    </Box>
  )
}

const MediaPreviewDialog: React.FC<{
  open: boolean
  items: AssistantPhotoItem[]
  startIndex: number
  onClose: () => void
}> = ({ open, items, startIndex, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(startIndex)
  const [isDownloading, setIsDownloading] = useState(false)
  const current = items[activeIndex]
  const { url, status } = useStorageDownloadUrl(open ? current?.storagePath : undefined)
  const [imgFailed, setImgFailed] = useState(false)
  const pdf = current ? previewAsPdf(current) || imgFailed : false

  useEffect(() => {
    if (open) {
      setActiveIndex(startIndex)
      setImgFailed(false)
    }
  }, [open, startIndex])

  useEffect(() => {
    setImgFailed(false)
  }, [activeIndex])

  if (!current) return null

  const movePrevious = () => {
    setActiveIndex(prev => (prev - 1 + items.length) % items.length)
  }
  const moveNext = () => {
    setActiveIndex(prev => (prev + 1) % items.length)
  }

  const handleDownload = async () => {
    if (!url || isDownloading) return
    setIsDownloading(true)
    try {
      await downloadFromUrl(url, downloadFilename(current.storagePath, pdf))
    } catch (error) {
      console.error('Error downloading assistant media', error)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          height: 'min(860px, 92vh)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle>
        {kindLabel(current.kind)}
        {items.length > 1 ? ` · ${activeIndex + 1} / ${items.length}` : ''}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0 }}
      >
        <Box
          sx={{
            borderRadius: 1,
            overflowX: 'hidden',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            bgcolor: pdf ? 'grey.200' : 'grey.900',
            width: '100%',
            flex: 1,
            minHeight: 0,
            mb: 1.25,
          }}
        >
          {status === 'loading' || status === 'idle' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : status === 'error' || !url ? (
            <Typography variant='body2' color='error' sx={{ p: 2, textAlign: 'center' }}>
              Could not load this file
            </Typography>
          ) : pdf ? (
            <PdfScrollPreview url={url} title={current.caption || kindLabel(current.kind)} />
          ) : (
            <Box
              component='img'
              src={url}
              alt={current.caption || kindLabel(current.kind)}
              onError={() => setImgFailed(true)}
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            size='small'
            variant='outlined'
            startIcon={<ArrowBackIosNewIcon fontSize='small' />}
            onClick={movePrevious}
            disabled={items.length <= 1}
            sx={{ textTransform: 'none' }}
          >
            Previous
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Button
              size='small'
              variant='outlined'
              startIcon={<DownloadIcon fontSize='small' />}
              onClick={() => void handleDownload()}
              disabled={!url || isDownloading}
              sx={{ textTransform: 'none' }}
            >
              Download
            </Button>
            <Tooltip title='Open in new tab'>
              <span>
                <IconButton
                  size='small'
                  aria-label='Open in new tab'
                  disabled={!url}
                  onClick={() => {
                    if (!url) return
                    window.open(url, '_blank', 'noopener,noreferrer')
                  }}
                >
                  <OpenInNewIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Button
            size='small'
            variant='outlined'
            endIcon={<ArrowForwardIosIcon fontSize='small' />}
            onClick={moveNext}
            disabled={items.length <= 1}
            sx={{ textTransform: 'none' }}
          >
            Next
          </Button>
        </Box>
        {current.caption ? (
          <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
            {current.caption}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

interface AssistantImageCardProps {
  card: AssistantImageCardData
}

export const AssistantImageCard: React.FC<AssistantImageCardProps> = ({ card }) => {
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [startIndex, setStartIndex] = useState(0)

  const mediaItems = useMemo(
    () => (card.items || []).filter(item => item.kind !== 'video'),
    [card.items],
  )

  const openAt = (index: number) => {
    setStartIndex(index)
    setGalleryOpen(true)
  }

  if (card.error && mediaItems.length === 0) {
    return (
      <Box
        sx={{
          mt: 1.25,
          p: 1.25,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, fontSize: { xs: '0.8125rem', sm: undefined } }}
        >
          {card.title || 'Photos'}
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ fontSize: { xs: '0.75rem', sm: undefined } }}
        >
          {card.error}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          mt: 1.25,
          p: 1.25,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.8125rem', sm: undefined } }}
        >
          {card.title || 'Photos'}
        </Typography>
        {card.vehicleLabel ? (
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
            {card.vehicleLabel}
          </Typography>
        ) : null}
        {mediaItems.length === 0 ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ fontSize: { xs: '0.75rem', sm: undefined } }}
          >
            {card.note || 'No photos found for this record.'}
          </Typography>
        ) : (
          <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
            {mediaItems.map((item, index) => (
              <Thumb
                key={`${item.storagePath}-${index}`}
                item={item}
                index={index}
                total={mediaItems.length}
                onOpen={openAt}
              />
            ))}
          </Stack>
        )}
        {card.truncated ? (
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1 }}>
            Showing {mediaItems.length} of {card.totalFound}. Open the linked portal page for the
            full set.
          </Typography>
        ) : null}
      </Box>
      <MediaPreviewDialog
        open={galleryOpen}
        items={mediaItems}
        startIndex={startIndex}
        onClose={() => setGalleryOpen(false)}
      />
    </>
  )
}

interface AssistantImageListProps {
  cards: AssistantImageCardData[]
}

export const AssistantImageList: React.FC<AssistantImageListProps> = ({ cards }) => {
  if (!cards.length) return null
  return (
    <Stack spacing={0.5}>
      {cards.map((card, index) => (
        <AssistantImageCard
          key={`${card.title}-${card.items?.[0]?.storagePath ?? index}`}
          card={card}
        />
      ))}
    </Stack>
  )
}
